import { Component, OnInit, inject, signal } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { VendorsService } from '@app/services/vendors.service/vendors.service';

@Component({
  standalone: true,
  selector: 'formly-file-upload-with-template',
  templateUrl: './file-upload-with-template.type.html',
  imports: [CommonModule, FormlyModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
})
export class FileUploadWithTemplateComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private vendorsService = inject(VendorsService);

  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  selectedFile: File | null = null;
  isDownloading = signal<boolean>(false);
  fileSizeError: string | null = null;

  get isReadonly(): boolean {
    return this.formState?.readonly === true;
  }

  get vendorId(): number | null {
    return this.formState?.vendorId || null;
  }

  ngOnInit(): void {
    // If there's already a file object stored (from previous selection), restore it
    if (this.formState?.files && this.key) {
      const existingFile = this.formState.files[this.key as string];
      if (existingFile instanceof File) {
        this.selectedFile = existingFile;
      }
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Check file size
      if (file.size > this.MAX_FILE_SIZE) {
        this.fileSizeError = `File exceeds maximum size of 10 MB (${this.formatFileSize(file.size)})`;
        this.selectedFile = null;
        input.value = '';
        this.formControl.setValue(null);
        this.formControl.markAsTouched();
        return;
      }

      this.fileSizeError = null;
      this.selectedFile = file;

      // Store the file in formState.files for later retrieval during submission
      if (!this.formState.files) {
        this.formState.files = {};
      }
      this.formState.files[this.key as string] = this.selectedFile;

      // Set a placeholder value to indicate a file is selected (for validation)
      this.formControl.setValue(`file:${this.selectedFile.name}`);
      this.formControl.markAsTouched();
    }
  }

  clearFile(fileInput: HTMLInputElement): void {
    this.selectedFile = null;
    this.fileSizeError = null;
    fileInput.value = '';

    // Remove from formState.files
    if (this.formState?.files) {
      delete this.formState.files[this.key as string];
    }

    this.formControl.setValue(null);
    this.formControl.markAsTouched();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileName(): string {
    const value = this.formControl.value;
    if (!value) return '';

    const path = value.split('/');
    const filename = path[path.length - 1];
    return filename;
  }

  downloadSubmittedFile(): void {
    if (!this.vendorId || !this.key) {
      console.error('Cannot download: vendorId or field key is missing');
      return;
    }

    this.isDownloading.set(true);
    this.vendorsService.getVendorFileDownloadUrl(this.vendorId, this.key as string).subscribe({
      next: (url) => {
        // Create a temporary anchor to trigger download without opening a new tab
        const link = document.createElement('a');
        link.href = url;
        link.download = this.getFileName() || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.isDownloading.set(false);
      },
      error: (err) => {
        console.error('Failed to get download URL:', err);
        this.isDownloading.set(false);
      },
    });
  }
}
